import {
  FormControl,
  FormHelperText,
  SelectChangeEvent,
  Autocomplete,
  Box,
} from '@mui/material';
import { Tooltip } from '@components/atoms';
import { Select, InputLabel, MenuItem, TextField } from '@components/atoms';
import { SingleSelectProps } from './SingleSelect.props';
import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export const SingleSelect = ({
  label,
  options,
  value,
  onChange,
  onInputChange,
  onFocus,
  disabled = false,
  error = false,
  helperText,
  required = false,
  formControlProps,
  selectProps,
  InputLabelProps,
  placeholder,
  labelIcon,
  labelTooltip,
  tooltipProps,
  searchable = false,
  loading = false,
  loadingText = 'Loading...',
}: SingleSelectProps) => {
  const labelId = `single-select-${label?.replace(/\s+/g, '-').toLowerCase()}`;
  const [inputValue, setInputValue] = useState('');

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    if (onChange) {
      onChange(event.target.value as string | number);
    }
  };

  if (searchable) {
    return (
      <Box {...formControlProps}>
        <Autocomplete
          fullWidth
          options={options}
          disableClearable
          loading={loading}
          loadingText={loadingText}
          inputValue={inputValue}
          onInputChange={(_, newInputValue, reason) => {
            // Only update input value when user is typing, not on reset or blur
            if (reason === 'input') {
              setInputValue(newInputValue);
              if (onInputChange) {
                onInputChange(newInputValue);
              }
            } else if (reason === 'reset' || reason === 'clear') {
              setInputValue('');
            }
          }}
          getOptionLabel={(option) =>
            typeof option === 'object' && 'label' in option
              ? String(option.label)
              : ''
          }
          value={undefined}
          onChange={(_, newValue) => {
            if (
              onChange &&
              newValue &&
              typeof newValue === 'object' &&
              'value' in newValue
            ) {
              onChange(newValue.value as string | number);
              // Clear the input after selection
              setInputValue('');
            }
          }}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={error}
              required={required}
              onFocus={onFocus}
              slotProps={{
                inputLabel: {
                  ...params.InputLabelProps,
                  ...InputLabelProps,
                  shrink: true,
                },
                input: {
                  ...params.InputProps,
                  placeholder: placeholder,
                },
              }}
            />
          )}
          popupIcon={<KeyboardArrowDownIcon />}
          isOptionEqualToValue={(option, value) =>
            typeof option === 'object' &&
            'value' in option &&
            typeof value === 'object' &&
            'value' in value &&
            option.value === value.value
          }
          slotProps={{
            listbox: {
              style: {
                maxHeight: '240px',
              },
            },
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
            },
            paper: {
              sx: {
                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
              },
            },
          }}
        />
        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </Box>
    );
  }

  return (
    <FormControl
      fullWidth
      error={error}
      disabled={disabled}
      required={required}
      {...formControlProps}
    >
      {label && (
        <InputLabel
          id={labelId}
          {...InputLabelProps}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            backgroundColor: 'background.paper',
            px: 0.5,
            ...InputLabelProps?.sx,
          }}
        >
          <Box component="span">{label}</Box>
          {labelIcon &&
            (labelTooltip ? (
              <Tooltip
                title={labelTooltip}
                arrow
                placement="bottom-start"
                {...tooltipProps}
              >
                <Box component="span" sx={{ display: 'inline-flex', ml: 0.5 }}>
                  {labelIcon}
                </Box>
              </Tooltip>
            ) : (
              <Box component="span" sx={{ display: 'inline-flex', ml: 0.5 }}>
                {labelIcon}
              </Box>
            ))}
        </InputLabel>
      )}
      <Select
        labelId={labelId}
        value={value ?? ''}
        onChange={handleChange}
        label={label}
        displayEmpty
        {...selectProps}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default SingleSelect;
